import React, { useMemo, useCallback, useState } from "react";
import { ComponentType } from "@/types";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useToast } from "@/hooks/use-toast";
import { useSlashCommands } from "@/services/ai/commands/useSlashCommands";
import type { ComponentDefaultSourceContext } from "@/services/ai/commands/utils/component-defaults";
import {
  Type as TextIcon,
  Image,
  Square,
  MousePointer,
  BarChart3,
  Calendar,
  ChartArea,
  ChartLine,
  ChartNoAxesCombined,
  ChartNoAxesColumn,
  ChartPie,
  ChartScatter,
  Clock,
  Columns3,
  FolderKanban,
  GitPullRequest,
  Kanban,
  List,
  ListChecks,
  ListTodo,
  MessageSquareText,
  MousePointerClick,
  Palette,
  PanelTop,
  StepForward,
  Table,
  Table2,
  Search,
  Star,
  Users,
  Vote,
  FolderOpen,
  Activity,
  BatteryMedium,
  CircleDot,
  Gauge,
  Lightbulb,
  SlidersHorizontal,
  Thermometer,
  ToggleLeft,
  Heading,
  TextCursorInput,
  RectangleHorizontal,
  Rows,
  Menu,
  Map as MapIcon,
  X,
} from "lucide-react";

interface ComponentDefinition {
  id: string;
  type: ComponentType;
  label: string;
  description: string;
  slashCommand: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  category: string;
  searchTerms?: string[];
  defaultProperties: Record<string, unknown>;
  defaultSize: { width: number; height: number };
  defaultStyle: Record<string, unknown>;
}

const manualOnlyHomeAssistantPlaceholder = {
  type: "manualOnly",
  manualOnly: true,
  safetyNote:
    "Palette placeholder only. Bind Home Assistant metadata for inspection; no service call is executed by this component.",
};

const chartDefaultStyle = {
  backgroundColor: "#ffffff",
  border: "1px solid #e0e0e0",
};

export const componentDefinitions: ComponentDefinition[] = [
  {
    id: "text",
    type: ComponentType.TEXT,
    label: "Text",
    description: "Add text content",
    slashCommand: "text",
    icon: TextIcon,
    category: "Basic",
    defaultProperties: {
      content: "Sample text",
      fontSize: 16,
      fontWeight: "normal",
      textAlign: "left",
    },
    defaultSize: { width: 200, height: 40 },
    defaultStyle: {
      color: "#000000",
      backgroundColor: "transparent",
    },
  },
  {
    id: "container",
    type: ComponentType.FLEX_CONTAINER,
    label: "Flex Container",
    description: "Group components with flex layout",
    slashCommand: "container",
    icon: Square,
    category: "Basic",
    defaultProperties: {
      padding: 16,
      backgroundColor: "#f5f5f5",
    },
    defaultSize: { width: 300, height: 200 },
    defaultStyle: {
      border: "1px solid #e0e0e0",
      borderRadius: 4,
    },
  },
  {
    id: "button",
    type: ComponentType.BUTTON,
    label: "Button",
    description: "Interactive button",
    slashCommand: "button",
    icon: MousePointer,
    category: "Basic",
    defaultProperties: {
      text: "Click me",
      variant: "primary",
      disabled: false,
    },
    defaultSize: { width: 120, height: 40 },
    defaultStyle: {
      backgroundColor: "#3b82f6",
      color: "#ffffff",
    },
  },
  {
    id: "image",
    type: ComponentType.IMAGE,
    label: "Image",
    description: "Display images",
    slashCommand: "image",
    icon: Image,
    category: "Basic",
    defaultProperties: {
      src: "",
      alt: "Image placeholder",
      objectFit: "cover",
    },
    defaultSize: { width: 300, height: 200 },
    defaultStyle: {},
  },
  {
    id: "icon",
    type: ComponentType.ICON,
    label: "Icon",
    description: "Add scalable icons",
    slashCommand: "icon",
    icon: Star,
    category: "Basic",
    defaultProperties: {
      iconName: "Star",
      library: "lucide",
      size: 24,
      color: "#000000",
      strokeWidth: 2,
      rotation: 0,
      opacity: 1,
      label: "Star icon",
    },
    defaultSize: { width: 48, height: 48 },
    defaultStyle: {
      backgroundColor: "transparent",
    },
  },
  {
    id: "heading",
    type: ComponentType.TEXT,
    label: "Heading",
    description: "Page or section heading",
    slashCommand: 'text "Heading"',
    icon: Heading,
    category: "Layout",
    searchTerms: ["h1", "h2", "title", "headline"],
    defaultProperties: {
      content: "Heading",
      fontSize: 32,
      fontWeight: "bold",
      textAlign: "left",
    },
    defaultSize: { width: 300, height: 48 },
    defaultStyle: {
      color: "#111827",
      backgroundColor: "transparent",
    },
  },
  {
    id: "input",
    type: ComponentType.INPUT,
    label: "Input",
    description: "Text input field",
    slashCommand: "input",
    icon: TextCursorInput,
    category: "Layout",
    searchTerms: ["form", "field", "text input", "input"],
    defaultProperties: {
      placeholder: "Enter text...",
      label: "Label",
      inputType: "text",
      required: false,
    },
    defaultSize: { width: 280, height: 64 },
    defaultStyle: {
      backgroundColor: "#ffffff",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
    },
  },
  {
    id: "card",
    type: ComponentType.CARD,
    label: "Card",
    description: "Bordered content card",
    slashCommand: "card",
    icon: RectangleHorizontal,
    category: "Layout",
    searchTerms: ["card", "panel", "tile", "container"],
    defaultProperties: {
      padding: 16,
    },
    defaultSize: { width: 320, height: 200 },
    defaultStyle: {
      backgroundColor: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    },
  },
  {
    id: "section",
    type: ComponentType.FLEX_CONTAINER,
    label: "Section",
    description: "Full-width page section",
    slashCommand: "container",
    icon: Rows,
    category: "Layout",
    searchTerms: ["section", "block", "row", "layout"],
    defaultProperties: {
      padding: 32,
      flexDirection: "column",
      gap: 16,
    },
    defaultSize: { width: 800, height: 300 },
    defaultStyle: {
      backgroundColor: "#f9fafb",
      border: "none",
      borderRadius: "0px",
      width: "100%",
    },
  },
  {
    id: "navigation",
    type: ComponentType.FLEX_CONTAINER,
    label: "Navigation",
    description: "Page header or navigation bar",
    slashCommand: "container",
    icon: Menu,
    category: "Layout",
    searchTerms: ["nav", "navbar", "header", "navigation", "menu"],
    defaultProperties: {
      padding: 16,
      flexDirection: "row",
      alignItems: "center",
      gap: 24,
      justifyContent: "space-between",
    },
    defaultSize: { width: 800, height: 64 },
    defaultStyle: {
      backgroundColor: "#ffffff",
      borderBottom: "1px solid #e5e7eb",
      width: "100%",
    },
  },
  {
    id: "push-button",
    type: ComponentType.PUSH_BUTTON,
    label: "Push Button",
    description: "Momentary press button control",
    slashCommand: "push button",
    icon: MousePointerClick,
    category: "Controls",
    searchTerms: ["button", "push", "momentary", "control"],
    defaultProperties: {
      label: "Button",
      theme: "power",
      shape: "circular",
    },
    defaultSize: { width: 80, height: 80 },
    defaultStyle: {},
  },
  {
    id: "icon-button",
    type: ComponentType.ICON_BUTTON,
    label: "Icon Button",
    description: "Compact icon-based action button",
    slashCommand: "icon button",
    icon: Lightbulb,
    category: "Controls",
    searchTerms: ["button", "icon", "action", "control"],
    defaultProperties: {
      iconName: "Power",
      label: "",
      theme: "ghost",
      isActive: false,
    },
    defaultSize: { width: 60, height: 60 },
    defaultStyle: {},
  },
  {
    id: "radial-dial",
    type: ComponentType.RADIAL_DIAL,
    label: "Radial Dial",
    description: "Circular value control",
    slashCommand: "radial dial",
    icon: SlidersHorizontal,
    category: "Controls",
    searchTerms: ["dial", "radial", "knob", "control"],
    defaultProperties: {
      value: 50,
      min: 0,
      max: 100,
      step: 1,
    },
    defaultSize: { width: 200, height: 200 },
    defaultStyle: {},
  },
  {
    id: "toggle-switch",
    type: ComponentType.TOGGLE_SWITCH,
    label: "Toggle Switch",
    description: "Binary on/off control",
    slashCommand: "toggle switch",
    icon: ToggleLeft,
    category: "Controls",
    searchTerms: ["toggle", "switch", "boolean", "control"],
    defaultProperties: {
      isOn: false,
      label: "Switch",
      theme: "ios",
    },
    defaultSize: { width: 70, height: 46 },
    defaultStyle: {},
  },
  {
    id: "stepper",
    type: ComponentType.STEPPER,
    label: "Stepper",
    description: "Increment or decrement numeric values",
    slashCommand: "stepper",
    icon: StepForward,
    category: "Controls",
    searchTerms: ["stepper", "counter", "number", "increment"],
    defaultProperties: {
      value: 0,
      min: 0,
      max: 100,
      step: 1,
    },
    defaultSize: { width: 200, height: 60 },
    defaultStyle: {},
  },
  {
    id: "slider",
    type: ComponentType.SLIDER,
    label: "Slider",
    description: "Range value control",
    slashCommand: "slider",
    icon: SlidersHorizontal,
    category: "Controls",
    searchTerms: ["slider", "range", "control"],
    defaultProperties: {
      value: 50,
      min: 0,
      max: 100,
      step: 1,
    },
    defaultSize: { width: 250, height: 60 },
    defaultStyle: {},
  },
  {
    id: "segmented-control",
    type: ComponentType.SEGMENTED_CONTROL,
    label: "Segmented Control",
    description: "Choose one option from grouped segments",
    slashCommand: "segmented control",
    icon: Columns3,
    category: "Controls",
    searchTerms: ["segmented", "tabs", "selector", "control"],
    defaultProperties: {
      title: "Control",
      segments: [
        { id: "opt1", label: "Option 1" },
        { id: "opt2", label: "Option 2" },
      ],
      selectedIndex: 0,
      theme: "ios",
      size: "md",
      equalWidth: true,
    },
    defaultSize: { width: 300, height: 50 },
    defaultStyle: {},
  },
  {
    id: "color-picker",
    type: ComponentType.COLOR_PICKER,
    label: "Color Picker",
    description: "Pick and adjust colors",
    slashCommand: "color picker",
    icon: Palette,
    category: "Controls",
    searchTerms: ["color", "colour", "picker", "swatch"],
    defaultProperties: {
      title: "Color",
      color: "#FF0000",
      mode: "wheel",
      theme: "compact",
    },
    defaultSize: { width: 200, height: 200 },
    defaultStyle: {},
  },
  {
    id: "thermostat",
    type: ComponentType.THERMOSTAT,
    label: "Thermostat",
    description: "Temperature setpoint control",
    slashCommand: "thermostat",
    icon: Thermometer,
    category: "Controls",
    searchTerms: ["thermostat", "temperature", "climate", "hvac"],
    defaultProperties: {
      temperature: 72,
      targetTemperature: 72,
      mode: "auto",
    },
    defaultSize: { width: 220, height: 260 },
    defaultStyle: {},
  },
  {
    id: "status-light",
    type: ComponentType.STATUS_LIGHT,
    label: "Status Light",
    description: "Visual state indicator",
    slashCommand: "status light",
    icon: CircleDot,
    category: "Controls",
    searchTerms: ["status", "light", "indicator", "led"],
    defaultProperties: {
      status: "unknown",
      label: "Status",
      theme: "flat",
    },
    defaultSize: { width: 80, height: 100 },
    defaultStyle: {},
  },
  {
    id: "level-indicator",
    type: ComponentType.LEVEL_INDICATOR,
    label: "Level Indicator",
    description: "Segmented level or battery display",
    slashCommand: "level indicator",
    icon: BatteryMedium,
    category: "Controls",
    searchTerms: ["level", "battery", "signal", "indicator"],
    defaultProperties: {
      value: 75,
      segments: 10,
      min: 0,
      max: 100,
    },
    defaultSize: { width: 60, height: 150 },
    defaultStyle: {},
  },
  {
    id: "modal",
    type: ComponentType.MODAL,
    label: "Modal",
    description: "Dialog overlay with trigger",
    slashCommand: "modal",
    icon: PanelTop,
    category: "Workflow",
    searchTerms: ["modal", "dialog", "popup", "overlay"],
    defaultProperties: {
      title: "Modal",
      triggerText: "Open Modal",
      size: "md",
    },
    defaultSize: { width: 400, height: 300 },
    defaultStyle: {},
  },
  {
    id: "questionnaire",
    type: ComponentType.QUESTIONNAIRE,
    label: "Questionnaire",
    description: "Multi-step survey or form",
    slashCommand: "questionnaire",
    icon: MessageSquareText,
    category: "Workflow",
    searchTerms: ["questionnaire", "survey", "quiz", "form"],
    defaultProperties: {
      title: "Questionnaire",
      viewMode: "form",
      showProgress: true,
    },
    defaultSize: { width: 600, height: 500 },
    defaultStyle: {},
  },
  {
    id: "voting-card",
    type: ComponentType.VOTING_CARD,
    label: "Voting Card",
    description: "Collect votes on a set of options",
    slashCommand: "voting card",
    icon: Vote,
    category: "Workflow",
    searchTerms: ["vote", "voting", "poll", "decision"],
    defaultProperties: {
      title: "Vote",
      voteType: "approve-veto",
      optionLayout: "list",
    },
    defaultSize: { width: 500, height: 400 },
    defaultStyle: {},
  },
  {
    id: "bar-chart",
    type: ComponentType.BAR_CHART,
    label: "Bar Chart",
    description: "Compare values across categories",
    slashCommand: "bar chart",
    icon: BarChart3,
    category: "Data",
    searchTerms: ["chart", "bar", "column", "comparison"],
    defaultProperties: {
      title: "Bar Chart",
      data: [],
      showLegend: true,
    },
    defaultSize: { width: 400, height: 400 },
    defaultStyle: chartDefaultStyle,
  },
  {
    id: "line-chart",
    type: ComponentType.LINE_CHART,
    label: "Line Chart",
    description: "Show trends over time",
    slashCommand: "line chart",
    icon: ChartLine,
    category: "Data",
    searchTerms: ["chart", "line", "trend", "time series"],
    defaultProperties: {
      title: "Line Chart",
      data: [],
      showLegend: true,
    },
    defaultSize: { width: 400, height: 400 },
    defaultStyle: chartDefaultStyle,
  },
  {
    id: "pie-chart",
    type: ComponentType.PIE_CHART,
    label: "Pie Chart",
    description: "Show proportions of a whole",
    slashCommand: "pie chart",
    icon: ChartPie,
    category: "Data",
    searchTerms: ["chart", "pie", "proportion", "share"],
    defaultProperties: {
      title: "Pie Chart",
      data: [],
      showLegend: true,
    },
    defaultSize: { width: 400, height: 400 },
    defaultStyle: chartDefaultStyle,
  },
  {
    id: "area-chart",
    type: ComponentType.AREA_CHART,
    label: "Area Chart",
    description: "Show cumulative trends",
    slashCommand: "area chart",
    icon: ChartArea,
    category: "Data",
    searchTerms: ["chart", "area", "trend", "stacked"],
    defaultProperties: {
      title: "Area Chart",
      data: [],
      showLegend: true,
    },
    defaultSize: { width: 400, height: 400 },
    defaultStyle: chartDefaultStyle,
  },
  {
    id: "scatter-chart",
    type: ComponentType.SCATTER_CHART,
    label: "Scatter Chart",
    description: "Plot correlation between values",
    slashCommand: "scatter chart",
    icon: ChartScatter,
    category: "Data",
    searchTerms: ["chart", "scatter", "plot", "correlation"],
    defaultProperties: {
      title: "Scatter Chart",
      data: [],
      showLegend: true,
    },
    defaultSize: { width: 400, height: 400 },
    defaultStyle: chartDefaultStyle,
  },
  {
    id: "heatmap-chart",
    type: ComponentType.HEATMAP_CHART,
    label: "Heatmap Chart",
    description: "Visualize intensity across two dimensions",
    slashCommand: "heatmap chart",
    icon: ChartNoAxesCombined,
    category: "Data",
    searchTerms: ["chart", "heatmap", "matrix", "intensity"],
    defaultProperties: {
      title: "Heatmap Chart",
      data: [],
      showLegend: true,
    },
    defaultSize: { width: 400, height: 400 },
    defaultStyle: chartDefaultStyle,
  },
  {
    id: "treemap-chart",
    type: ComponentType.TREEMAP_CHART,
    label: "Treemap Chart",
    description: "Compare nested category sizes",
    slashCommand: "treemap chart",
    icon: ChartNoAxesColumn,
    category: "Data",
    searchTerms: ["chart", "treemap", "hierarchy", "nested"],
    defaultProperties: {
      title: "Treemap Chart",
      data: [],
      showLegend: true,
    },
    defaultSize: { width: 400, height: 400 },
    defaultStyle: chartDefaultStyle,
  },
  {
    id: "gauge-chart",
    type: ComponentType.GAUGE_CHART,
    label: "Gauge Chart",
    description: "Display a value against a range",
    slashCommand: "gauge chart",
    icon: Gauge,
    category: "Data",
    searchTerms: ["chart", "gauge", "meter", "progress"],
    defaultProperties: {
      title: "Gauge Chart",
      value: 65,
      min: 0,
      max: 100,
      unit: "%",
    },
    defaultSize: { width: 200, height: 200 },
    defaultStyle: chartDefaultStyle,
  },
  {
    id: "vertical-gauge-chart",
    type: ComponentType.VERTICAL_GAUGE_CHART,
    label: "Vertical Gauge",
    description: "Display a vertical level value",
    slashCommand: "vertical-gauge chart",
    icon: Gauge,
    category: "Data",
    searchTerms: ["chart", "vertical gauge", "meter", "level"],
    defaultProperties: {
      title: "Vertical Gauge",
      value: 65,
      min: 0,
      max: 100,
      unit: "%",
    },
    defaultSize: { width: 200, height: 300 },
    defaultStyle: chartDefaultStyle,
  },
  {
    id: "map-chart",
    type: ComponentType.MAP_CHART,
    label: "Map Chart",
    description: "Show geospatial data on a map",
    slashCommand: "map chart",
    icon: MapIcon,
    category: "Data",
    searchTerms: ["chart", "map", "geo", "location", "pins"],
    defaultProperties: {
      title: "Map Chart",
      data: [],
    },
    defaultSize: { width: 500, height: 400 },
    defaultStyle: chartDefaultStyle,
  },
  {
    id: "table",
    type: ComponentType.TABLE,
    label: "Table",
    description: "Display data in tables",
    slashCommand: "table",
    icon: Table,
    category: "Data",
    defaultProperties: {
      columns: [],
      data: [],
    },
    defaultSize: { width: 500, height: 300 },
    defaultStyle: {
      backgroundColor: "#ffffff",
      border: "1px solid #e0e0e0",
    },
  },
  {
    id: "advanced-table",
    type: ComponentType.ADVANCED_TABLE,
    label: "Advanced Table",
    description: "Data table with selection and resizing",
    slashCommand: "advanced table",
    icon: Table2,
    category: "Data",
    searchTerms: ["table", "advanced", "grid", "data"],
    defaultProperties: {
      columns: [],
      data: [],
      enableColumnResizing: true,
      enableRowSelection: true,
    },
    defaultSize: { width: 600, height: 400 },
    defaultStyle: {
      backgroundColor: "#ffffff",
      border: "1px solid #e0e0e0",
    },
  },
  {
    id: "list",
    type: ComponentType.LIST,
    label: "List",
    description: "Simple list of items",
    slashCommand: "list",
    icon: List,
    category: "Data",
    searchTerms: ["list", "items", "collection"],
    defaultProperties: {
      items: [],
    },
    defaultSize: { width: 200, height: 100 },
    defaultStyle: {},
  },
  {
    id: "file-storage",
    type: ComponentType.FILE_STORAGE,
    label: "File Storage",
    description: "Upload and manage files",
    slashCommand: "file storage",
    icon: FolderOpen,
    category: "Data",
    defaultProperties: {
      title: "File Storage",
      displayMode: "grid",
      uploadEnabled: true,
      deleteEnabled: true,
      showSearch: true,
      maxFileSize: 10485760,
    },
    defaultSize: { width: 400, height: 300 },
    defaultStyle: {
      backgroundColor: "#ffffff",
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
    },
  },
  {
    id: "kanban-board",
    type: ComponentType.KANBAN_BOARD,
    label: "Kanban Board",
    description: "Column-based task board",
    slashCommand: "kanban board",
    icon: Kanban,
    category: "Planning",
    searchTerms: ["kanban", "board", "tasks", "pipeline"],
    defaultProperties: {
      columns: [],
      data: [],
      sampleData: [],
    },
    defaultSize: { width: 800, height: 500 },
    defaultStyle: {},
  },
  {
    id: "gantt-chart",
    type: ComponentType.GANTT_CHART,
    label: "Gantt Chart",
    description: "Project timeline with dependencies",
    slashCommand: "gantt chart",
    icon: GitPullRequest,
    category: "Planning",
    searchTerms: ["gantt", "timeline", "schedule", "project"],
    defaultProperties: {
      timeScale: "week",
      data: [],
      sampleData: [],
    },
    defaultSize: { width: 900, height: 400 },
    defaultStyle: {},
  },
  {
    id: "calendar",
    type: ComponentType.CALENDAR,
    label: "Calendar",
    description: "Event calendar and scheduler",
    slashCommand: "calendar",
    icon: Calendar,
    category: "Planning",
    searchTerms: ["calendar", "schedule", "events", "agenda"],
    defaultProperties: {
      viewMode: "month",
      events: [],
      sampleData: [],
    },
    defaultSize: { width: 800, height: 600 },
    defaultStyle: {},
  },
  {
    id: "checklist",
    type: ComponentType.CHECKLIST,
    label: "Checklist",
    description: "Track completion of task items",
    slashCommand: "checklist",
    icon: ListChecks,
    category: "Planning",
    searchTerms: ["checklist", "todo", "tasks", "list"],
    defaultProperties: {
      items: [],
      showProgress: true,
    },
    defaultSize: { width: 400, height: 300 },
    defaultStyle: {},
  },
  {
    id: "timeline",
    type: ComponentType.HORIZONTAL_TIMELINE,
    label: "Timeline",
    description: "Horizontal milestone timeline",
    slashCommand: "timeline",
    icon: ListTodo,
    category: "Planning",
    searchTerms: ["timeline", "roadmap", "milestones", "horizontal"],
    defaultProperties: {
      events: [],
      orientation: "horizontal",
      showConnectors: true,
    },
    defaultSize: { width: 800, height: 200 },
    defaultStyle: {},
  },
  {
    id: "assignment-widget",
    type: ComponentType.ASSIGNMENT_WIDGET,
    label: "Assignment Widget",
    description: "Show people, roles, and assigned work",
    slashCommand: "assignment widget",
    icon: Users,
    category: "Planning",
    searchTerms: ["assignment", "team", "tasks", "roster"],
    defaultProperties: {
      assignments: [],
      layout: "list",
      showTaskCount: true,
    },
    defaultSize: { width: 400, height: 300 },
    defaultStyle: {},
  },
  {
    id: "project-hub",
    type: ComponentType.PROJECT_HUB,
    label: "Project Hub",
    description: "Multi-view project workspace",
    slashCommand: "project hub",
    icon: FolderKanban,
    category: "Planning",
    searchTerms: ["project", "hub", "board", "workspace"],
    defaultProperties: {
      tabs: [],
      activeTab: "table",
      savedViews: [],
      sampleData: [],
    },
    defaultSize: { width: 900, height: 600 },
    defaultStyle: {},
  },
  {
    id: "time-tracker",
    type: ComponentType.TIME_TRACKER,
    label: "Time Tracker",
    description: "Track time and billable work",
    slashCommand: "time tracker",
    icon: Clock,
    category: "Planning",
    searchTerms: ["time", "tracker", "timer", "timesheet"],
    defaultProperties: {
      viewMode: "timer",
      defaultBillable: true,
      projects: [],
      data: [],
      sampleData: [],
    },
    defaultSize: { width: 900, height: 700 },
    defaultStyle: {},
  },
  {
    id: "ha-data-text",
    type: ComponentType.DATA_TEXT,
    label: "Entity State",
    description: "Read-only Home Assistant entity value",
    slashCommand: "data text",
    icon: Activity,
    category: "Home Assistant",
    searchTerms: ["ha", "sensor", "state", "temperature", "humidity", "energy"],
    defaultProperties: {
      label: "Entity State",
      value: "Waiting for binding",
      valueField: "value",
      readOnly: true,
    },
    defaultSize: { width: 180, height: 64 },
    defaultStyle: {
      backgroundColor: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      padding: "12px",
    },
  },
  {
    id: "ha-status-light",
    type: ComponentType.STATUS_LIGHT,
    label: "Status Light",
    description: "Door, motion, connectivity, or alarm status",
    slashCommand: "status light",
    icon: CircleDot,
    category: "Home Assistant",
    searchTerms: ["ha", "security", "motion", "door", "availability", "health"],
    defaultProperties: {
      label: "Status",
      status: "unknown",
      theme: "flat",
      shape: "circle",
      readOnly: true,
    },
    defaultSize: { width: 96, height: 96 },
    defaultStyle: {
      backgroundColor: "transparent",
    },
  },
  {
    id: "ha-level-indicator",
    type: ComponentType.LEVEL_INDICATOR,
    label: "Battery Level",
    description: "Battery, signal, tank, or level indicator",
    slashCommand: "level indicator",
    icon: BatteryMedium,
    category: "Home Assistant",
    searchTerms: ["ha", "battery", "device health", "signal", "level"],
    defaultProperties: {
      label: "Battery",
      value: 72,
      min: 0,
      max: 100,
      unit: "%",
      segments: 5,
      readOnly: true,
    },
    defaultSize: { width: 80, height: 140 },
    defaultStyle: {
      backgroundColor: "transparent",
    },
  },
  {
    id: "ha-gauge",
    type: ComponentType.GAUGE_CHART,
    label: "Entity Gauge",
    description: "Energy, power, humidity, or sensor gauge",
    slashCommand: "gauge chart",
    icon: Gauge,
    category: "Home Assistant",
    searchTerms: ["ha", "energy", "power", "humidity", "temperature", "sensor"],
    defaultProperties: {
      title: "Entity Gauge",
      value: 42,
      min: 0,
      max: 100,
      unit: "%",
      displayMode: "ring",
      readOnly: true,
    },
    defaultSize: { width: 220, height: 180 },
    defaultStyle: {
      backgroundColor: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      padding: "12px",
    },
  },
  {
    id: "ha-light-button",
    type: ComponentType.ICON_BUTTON,
    label: "Light Button",
    description: "Manual-only light control placeholder",
    slashCommand: "icon button",
    icon: Lightbulb,
    category: "Home Assistant",
    searchTerms: ["ha", "light", "lamp", "scene", "manual only"],
    defaultProperties: {
      label: "Light",
      iconName: "Lightbulb",
      library: "lucide",
      theme: "ghost",
      isActive: false,
      action: manualOnlyHomeAssistantPlaceholder,
    },
    defaultSize: { width: 96, height: 96 },
    defaultStyle: {
      backgroundColor: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
    },
  },
  {
    id: "ha-toggle",
    type: ComponentType.TOGGLE_SWITCH,
    label: "HA Toggle",
    description: "Manual-only switch placeholder",
    slashCommand: "toggle switch",
    icon: ToggleLeft,
    category: "Home Assistant",
    searchTerms: ["ha", "switch", "toggle", "manual only"],
    defaultProperties: {
      label: "Switch",
      isOn: false,
      theme: "ios",
      action: manualOnlyHomeAssistantPlaceholder,
    },
    defaultSize: { width: 96, height: 64 },
    defaultStyle: {
      backgroundColor: "transparent",
    },
  },
  {
    id: "ha-dimmer",
    type: ComponentType.RADIAL_DIAL,
    label: "Dimmer Dial",
    description: "Manual-only brightness control placeholder",
    slashCommand: "radial dial",
    icon: SlidersHorizontal,
    category: "Home Assistant",
    searchTerms: ["ha", "light", "brightness", "dimmer", "manual only"],
    defaultProperties: {
      label: "Brightness",
      value: 50,
      min: 0,
      max: 100,
      unit: "%",
      theme: "minimal",
      action: manualOnlyHomeAssistantPlaceholder,
    },
    defaultSize: { width: 180, height: 180 },
    defaultStyle: {
      backgroundColor: "transparent",
    },
  },
  {
    id: "ha-slider",
    type: ComponentType.SLIDER,
    label: "HA Slider",
    description: "Manual-only brightness or target value placeholder",
    slashCommand: "slider",
    icon: SlidersHorizontal,
    category: "Home Assistant",
    searchTerms: ["ha", "slider", "brightness", "target", "manual only"],
    defaultProperties: {
      label: "Target",
      value: 50,
      min: 0,
      max: 100,
      step: 1,
      unit: "%",
      action: manualOnlyHomeAssistantPlaceholder,
    },
    defaultSize: { width: 220, height: 64 },
    defaultStyle: {
      backgroundColor: "transparent",
    },
  },
  {
    id: "ha-thermostat",
    type: ComponentType.THERMOSTAT,
    label: "Thermostat",
    description: "Manual-only climate control placeholder",
    slashCommand: "thermostat",
    icon: Thermometer,
    category: "Home Assistant",
    searchTerms: ["ha", "climate", "hvac", "temperature", "manual only"],
    defaultProperties: {
      label: "Thermostat",
      temperature: 72,
      targetTemperature: 72,
      humidity: 45,
      min: 50,
      max: 90,
      unit: "fahrenheit",
      mode: "auto",
      theme: "nest",
      action: manualOnlyHomeAssistantPlaceholder,
    },
    defaultSize: { width: 260, height: 320 },
    defaultStyle: {
      backgroundColor: "transparent",
    },
  },
];

interface ComponentPaletteProps {
  className?: string;
  onClose?: () => void;
  sourceContext?: ComponentDefaultSourceContext;
}

export const ComponentPalette: React.FC<ComponentPaletteProps> = ({ className, onClose, sourceContext }) => {
  const { project, currentPageId } = useEditorStore();
  const { executeSlashCommand, isProcessing } = useSlashCommands();
  const [searchTerm, setSearchTerm] = useState("");
  const { toast } = useToast();

  const filteredComponents = useMemo(() => {
    if (!searchTerm) return componentDefinitions;

    const term = searchTerm.toLowerCase();
    return componentDefinitions.filter(
      comp =>
        comp.label.toLowerCase().includes(term) ||
        comp.description.toLowerCase().includes(term) ||
        comp.category.toLowerCase().includes(term) ||
        comp.searchTerms?.some((searchTerm) => searchTerm.includes(term))
    );
  }, [searchTerm]);

  const handleComponentClick = useCallback(async (component: ComponentDefinition) => {
    if (!project?.id || !currentPageId) {
      toast({
        title: "No active project",
        description: "Please create or open a project first.",
        variant: "destructive",
      });
      return;
    }

    try {
      const command = `/add ${component.slashCommand}`;
      const result =
        sourceContext === undefined
          ? await executeSlashCommand(command, null, true)
          : await executeSlashCommand(command, null, true, { sourceContext });

      if (!result.success) {
        toast({
          title: "Could not add component",
          description: result.message,
          variant: "destructive",
        });
        return;
      }

      result.applyMutations?.();

      toast({
        title: "Component Added",
        description: result.message || `${component.label} has been added. Use the AI chat to customize it.`,
      });
    } catch (error) {
      toast({
        title: "Could not add component",
        description: error instanceof Error ? error.message : "Unexpected error adding component.",
        variant: "destructive",
      });
    }
  }, [executeSlashCommand, project, currentPageId, sourceContext, toast]);

  const groupedComponents = useMemo(() => {
    const groups: Record<string, ComponentDefinition[]> = {};
    filteredComponents.forEach(comp => {
      if (!groups[comp.category]) {
        groups[comp.category] = [];
      }
      groups[comp.category].push(comp);
    });
    return groups;
  }, [filteredComponents]);

  return (
    <div className={cn("h-full flex flex-col bg-card", className)}>
      {/* Header */}
      <div className="px-4 pt-3 pb-2 border-b">
        {onClose && (
          <div className="flex justify-end mb-2">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close component palette"
              className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search components..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-8"
          />
        </div>
        <p className="text-xs mt-2 text-muted-foreground">
          Click to add • AI chat to customize
        </p>
      </div>

      {/* Component List */}
      <div className="flex-1 overflow-y-auto p-4">
        {Object.entries(groupedComponents).map(([category, components]) => (
          <div key={category} className="mb-6">
            <h4 className="text-sm font-medium mb-2 text-muted-foreground">
              {category}
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {components.map((component) => {
                const Icon = component.icon;
                return (
                  <button
                    key={component.id}
                    type="button"
                    disabled={isProcessing}
                    onClick={() => {
                      void handleComponentClick(component);
                    }}
                    className={cn(
                      "p-3 rounded-lg border bg-card",
                      "hover:bg-accent hover:border-accent-foreground/20",
                      "transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-60",
                      "flex flex-col items-center gap-2",
                      "group"
                    )}
                  >
                    <Icon className="h-5 w-5 text-muted-foreground group-hover:text-foreground" />
                    <span className="text-xs font-medium">{component.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* AI Hint */}
      <div className="p-4 border-t bg-muted/50">
        <p className="text-xs text-muted-foreground">
          Tip: Use the AI chat to quickly add and customize components with natural language commands.
        </p>
      </div>
    </div>
  );
};
